import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BookOpenIcon,
  LanguageIcon,
  StarIcon,
  ChevronLeftIcon,
  EyeIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BriefcaseIcon,
} from "@heroicons/react/16/solid"

import { DashboardLayout } from "../../layouts/DashboardLayout"
import { http } from "../../shared/api/http"
import {
  CourseDetail,
  getCourseApi,
  deleteCourseApi,
  updateCourseApi,
} from "../../features/courses/api/course.api"
import {
  CourseVacancyItem,
  listCourseVacanciesApi,
} from "../../features/courses/api/courseVacancies.api"
import {
  listModulesApi,
  ModuleListItem,
  deleteModuleApi,
} from "../../features/modules/api/modules.api"
import { ModuleModal } from "../../features/modules/ui/ModuleModal"
import { EditCourseModal } from "./EditCourseModal"
import { EditMentorCourseModal } from "./EditMentorCourseModal"

type Tab = "overview" | "modules" | "settings"

const TAB_LABELS: Record<Tab, string> = {
  overview: "Обзор",
  modules: "Модули",
  settings: "Настройки",
}

/* ── helpers ─────────────────────────────────────────── */

const withHttps = (url?: string) => {
  if (!url) return null
  return url.startsWith("http") ? url : `https://${url}`
}

type AvatarLike = string | { original_url?: string } | null | undefined

const avatarSrc = (avatar: AvatarLike) => {
  const url = typeof avatar === "string" ? avatar : avatar?.original_url
  if (!url) return null
  return url.startsWith("http") ? url : `https://${url}`
}

const InitialAvatar = ({ name, size = "sm" }: { name?: string; size?: "sm" | "md" }) => {
  const initials = name
    ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?"
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm"
  return (
    <div className={`${sizeClass} rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold flex-shrink-0`}>
      {initials}
    </div>
  )
}

const PriceBadge = ({ type, amount }: { type: "free" | "paid" | "mixed"; amount: number }) => {
  if (type === "free")
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Бесплатно</span>
  if (type === "mixed")
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">От ${amount}</span>
  return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">${amount}</span>
}

/* ── page ─────────────────────────────────────────────── */

export const CourseDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const role = localStorage.getItem("role")
  const isAdmin = role === "admin"
  const TABS: Tab[] = isAdmin ? ["overview", "modules", "settings"] : ["overview", "modules"]

  const [course, setCourse] = useState<CourseDetail | null>(null)
  const [modules, setModules] = useState<ModuleListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [modulesLoading, setModulesLoading] = useState(false)
  const [tab, setTab] = useState<Tab>("overview")
  const [openModuleModal, setOpenModuleModal] = useState(false)
  const [editModule, setEditModule] = useState<ModuleListItem | null>(null)
  const [openEditCourse, setOpenEditCourse] = useState(false)
  const [openEditMentorCourse, setOpenEditMentorCourse] = useState(false)
  const [togglingActive, setTogglingActive] = useState(false)

  const [vacancies, setVacancies] = useState<CourseVacancyItem[]>([])
  const [vacanciesLoading, setVacanciesLoading] = useState(true)

  const handleDeleteModule = async (moduleId: string) => {
    if (!window.confirm("Удалить модуль?")) return
    await deleteModuleApi(moduleId)
    refetchModules()
  }

  const handleToggleActive = async () => {
    if (!course || togglingActive) return
    const nextActive = !(course.is_active ?? true)
    setTogglingActive(true)
    try {
      await updateCourseApi(course.id, { is_active: nextActive })
      setCourse((prev) => (prev ? { ...prev, is_active: nextActive } : prev))
    } finally {
      setTogglingActive(false)
    }
  }

  const refetchCourse = () => {
    if (!id) return
    getCourseApi(id).then((res) => setCourse(res.data))
  }

  const refetchModules = () => {
    if (!id) return
    setModulesLoading(true)
    listModulesApi(id)
      .then((res: any) => {
        setModules(res.data.items.sort((a: ModuleListItem, b: ModuleListItem) => a.order_number - b.order_number))
      })
      .finally(() => setModulesLoading(false))
  }

  useEffect(() => {
    if (!id) return
    http.post(`/api/courses/${id}/view`).catch(() => {})
    setLoading(true)
    getCourseApi(id).then((res) => setCourse(res.data)).finally(() => setLoading(false))

    setVacanciesLoading(true)
    listCourseVacanciesApi(id)
      .then((res) => setVacancies(res.data.items))
      .catch(() => setVacancies([]))
      .finally(() => setVacanciesLoading(false))
  }, [id])

  useEffect(() => {
    if (!id || tab !== "modules") return
    refetchModules()
  }, [id, tab])

  /* ── loading ── */
  if (loading) {
    return (
      <DashboardLayout title="Курс">
        <div className="space-y-4 animate-pulse">
          <div className="h-56 rounded-2xl bg-gray-100" />
          <div className="flex gap-6 border-b pb-0 pt-2">
            {[1,2,3].map(i => <div key={i} className="h-4 w-20 bg-gray-100 rounded" />)}
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!course) {
    return (
      <DashboardLayout title="Курс">
        <div className="py-20 text-center text-gray-400">Курс не найден</div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title={course.title}>
      <div className="space-y-5">

        {/* ── COVER HERO ── */}
        <div className="relative rounded-2xl overflow-hidden border bg-gray-100 h-56">
          {withHttps(course.cover_image) ? (
            <img src={withHttps(course.cover_image)!} className="w-full h-full object-cover" alt={course.title} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
              <BookOpenIcon className="w-12 h-12 text-blue-200" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* back */}
          <button
            onClick={() => navigate("/courses")}
            className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-md ring-1 ring-black/5"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Курсы
          </button>

          {/* bottom info */}
          <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between text-white">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {course.is_promoted && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-400/90 text-amber-900">
                    <StarIcon className="w-3 h-3" />Продвигается
                  </span>
                )}
                {course.is_active === false && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/80 text-gray-200">
                    Неактивен
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold drop-shadow">{course.title}</h1>
              <div className="flex gap-3 text-sm mt-2 opacity-85 flex-wrap">
                <span className="flex items-center gap-1"><LanguageIcon className="w-3.5 h-3.5" />{course.language.toUpperCase()}</span>
                {course.duration_minutes && <span className="flex items-center gap-1"><ClockIcon className="w-3.5 h-3.5" />{course.duration_minutes} мин</span>}
                <span className="flex items-center gap-1"><AcademicCapIcon className="w-3.5 h-3.5" />{course.level}</span>
                <span className="flex items-center gap-1"><BookOpenIcon className="w-3.5 h-3.5" />{course.lessons_count} уроков</span>
              </div>
            </div>
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleActive}
                  disabled={togglingActive}
                  title={course.is_active === false ? "Сделать активным" : "Сделать неактивным"}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors shadow-md ring-1 ring-black/5 disabled:opacity-60 ${
                    course.is_active === false
                      ? "bg-white text-gray-600 hover:bg-gray-50"
                      : "bg-emerald-500 text-white hover:bg-emerald-600"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${course.is_active === false ? "bg-gray-400" : "bg-white"}`} />
                  {togglingActive ? "…" : course.is_active === false ? "Неактивен" : "Активен"}
                </button>
                <button
                  onClick={() => setOpenEditCourse(true)}
                  className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Редактировать
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOpenEditMentorCourse(true)}
                  className="inline-flex items-center gap-1.5 bg-white text-gray-800 px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors shadow-md ring-1 ring-black/5"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Редактировать
                </button>
                <button
                  onClick={() => navigate(`/mentor/analytics/courses/${course.id}`)}
                  className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md"
                >
                  <ChartBarIcon className="w-4 h-4" />
                  Моя аналитика
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                tab === t ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <OverviewTab course={course} vacancies={vacancies} vacanciesLoading={vacanciesLoading} />
        )}

        {/* ── MODULES ── */}
        {tab === "modules" && (
          <ModulesTab
            modules={modules}
            loading={modulesLoading}
            canManageStructure={isAdmin}
            onAdd={() => { setEditModule(null); setOpenModuleModal(true) }}
            onEdit={(m) => { setEditModule(m); setOpenModuleModal(true) }}
            onDelete={handleDeleteModule}
            onView={(moduleId) => navigate(`/courses/${course.id}/modules/${moduleId}`)}
          />
        )}

        {/* ── SETTINGS ── */}
        {tab === "settings" && isAdmin && (
          <SettingsTab courseId={course.id} onDeleted={() => navigate("/courses")} />
        )}
      </div>

      <ModuleModal
        open={openModuleModal}
        onClose={() => setOpenModuleModal(false)}
        courseId={course.id}
        module={editModule}
        isMentor={!isAdmin}
        onSaved={refetchModules}
      />

      <EditCourseModal
        open={openEditCourse}
        onClose={() => setOpenEditCourse(false)}
        course={course}
        onSaved={refetchCourse}
      />

      <EditMentorCourseModal
        open={openEditMentorCourse}
        onClose={() => setOpenEditMentorCourse(false)}
        course={course}
        onSaved={refetchCourse}
      />
    </DashboardLayout>
  )
}

/* ── overview tab ─────────────────────────────────────── */

const formatVacancySalary = (v: CourseVacancyItem) => {
  if (!v.salary_to) return null
  return `до ${v.salary_to.toLocaleString()} ${v.salary_currency ?? ""}`.trim()
}

const VacanciesBlock = ({
  vacancies,
  loading,
}: {
  vacancies: CourseVacancyItem[]
  loading: boolean
}) => (
  <div className="bg-white rounded-xl border p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
      <BriefcaseIcon className="w-4 h-4 text-gray-400" />
      Вакансии
    </h3>

    {loading ? (
      <div className="space-y-2 animate-pulse">
        {[1, 2].map((i) => <div key={i} className="h-14 bg-gray-100 rounded-lg" />)}
      </div>
    ) : vacancies.length === 0 ? (
      <p className="text-sm text-gray-400 italic">Пока нет вакансий, привязанных к этому курсу</p>
    ) : (
      <div className="space-y-2">
        {vacancies.map((v) => (
          <div key={v.id} className="flex items-start gap-3 rounded-lg border border-gray-100 px-3.5 py-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <BriefcaseIcon className="w-4 h-4 text-blue-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 truncate">{v.title}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {formatVacancySalary(v) && (
                  <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5">
                    {formatVacancySalary(v)}
                  </span>
                )}
                {v.employment_type && (
                  <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 capitalize">
                    {v.employment_type}
                  </span>
                )}
                {v.work_format && (
                  <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 capitalize">
                    {v.work_format}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)

const OverviewTab = ({
  course,
  vacancies,
  vacanciesLoading,
}: {
  course: CourseDetail
  vacancies: CourseVacancyItem[]
  vacanciesLoading: boolean
}) => (
  <div className="grid grid-cols-3 gap-5">
    <div className="col-span-2 space-y-5">
      {/* Description */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Описание</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{course.description || <span className="text-gray-400 italic">Нет описания</span>}</p>
      </div>

      {/* Sections */}
      {course.sections?.map((s) => (
        <div key={s.title} className="bg-white rounded-xl border p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{s.title}</h4>
          <ul className="space-y-2">
            {s.items.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* Vacancies */}
      <VacanciesBlock vacancies={vacancies} loading={vacanciesLoading} />
    </div>

    {/* Sidebar */}
    <div className="space-y-3">
      {/* Author */}
      <div className="bg-white rounded-xl border p-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2.5">Автор</p>
        <div className="flex items-center gap-2.5">
          {avatarSrc(course.author?.avatar) ? (
            <img src={avatarSrc(course.author.avatar)!} className="w-8 h-8 rounded-full object-cover" alt="" />
          ) : (
            <InitialAvatar name={course.author?.full_name} />
          )}
          <span className="text-sm font-medium text-gray-800">{course.author?.full_name || "Неизвестно"}</span>
        </div>
      </div>

      {/* Mentors */}
      <div className="bg-white rounded-xl border p-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2.5">Менторы</p>
        {course.mentors.length === 0 ? (
          <span className="text-sm text-gray-400 italic">Не назначены</span>
        ) : (
          <div className="space-y-2">
            {course.mentors.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {avatarSrc(m.avatar) ? (
                  <img src={avatarSrc(m.avatar)!} className="w-7 h-7 rounded-full object-cover" alt="" />
                ) : (
                  <InitialAvatar name={m.full_name} />
                )}
                <span className="text-sm text-gray-700">{m.full_name || "Неизвестно"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="bg-white rounded-xl border p-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Цена</p>
        <PriceBadge type={course.price.type} amount={course.price.amount} />
      </div>

      {/* Categories */}
      {course.categories?.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2.5">Категории</p>
          <div className="flex flex-wrap gap-1.5">
            {course.categories.map((c) => (
              <span key={c.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {c.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
)

/* ── modules tab ──────────────────────────────────────── */

const ModulesTab = ({
  modules, loading, canManageStructure, onAdd, onEdit, onDelete, onView,
}: {
  modules: ModuleListItem[]
  loading: boolean
  canManageStructure: boolean
  onAdd: () => void
  onEdit: (m: ModuleListItem) => void
  onDelete: (id: string) => void
  onView: (id: string) => void
}) => {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3].map(i => (
          <div key={i} className="bg-white rounded-xl border p-5 h-24" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {modules.map((m) => (
        <div key={m.id} className="group bg-white border rounded-xl p-5 hover:border-blue-200 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {m.order_number}
              </span>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 leading-snug">{m.title}</h3>
                {m.description && (
                  <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{m.description}</p>
                )}
                <div className="flex gap-3 mt-2.5 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <BookOpenIcon className="w-3.5 h-3.5" />{m.lessons_count} уроков
                  </span>
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" />{m.duration_minutes} мин
                  </span>
                  {m.has_free_lessons && (
                    <span className="text-emerald-600 font-medium">Есть бесплатные уроки</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
              <button
                onClick={() => onView(m.id)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <EyeIcon className="w-3.5 h-3.5" />Просмотр
              </button>
              <button
                onClick={() => onEdit(m)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />Редактировать
              </button>
              {canManageStructure && (
                <button
                  onClick={() => onDelete(m.id)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {canManageStructure && (
        <button
          onClick={onAdd}
          className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 text-sm font-medium text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          Добавить модуль
        </button>
      )}
    </div>
  )
}

/* ── settings tab ─────────────────────────────────────── */

const SettingsTab = ({ courseId, onDeleted }: { courseId: string; onDeleted: () => void }) => {
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    if (!window.confirm("Вы уверены, что хотите удалить этот курс?\nЭто действие необратимо.")) return
    try {
      setLoading(true)
      await deleteCourseApi(courseId)
      onDeleted()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-lg">
      <div className="rounded-xl border border-red-200 bg-red-50 p-5">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-700 text-sm">Опасная зона</h3>
            <p className="text-sm text-red-600 mt-1 leading-relaxed">
              Удаление курса необратимо. Все модули, уроки и связанные данные будут удалены навсегда.
            </p>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <TrashIcon className="w-4 h-4" />
              {loading ? "Удаление…" : "Удалить курс"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
